// ── Entity-image optimization ─────────────────────────────────────────────────
// Mirrors the Eurynote profile-picture approach (resize into a box, then step the
// JPEG quality down), but tuned for a smaller upload target. Runs entirely in the
// browser via <canvas>, so no extra dependency is pulled in.

/** Primary resize box. Aspect ratio is preserved within these bounds; images are never upscaled. */
const MAX_DIMENSION = 800;

/** Fallback box for very large photos still above target after the primary quality passes. */
const FALLBACK_DIMENSION = 512;

/** Target upload size. We step quality down until the encoded blob fits under this. */
const TARGET_BYTES = 100 * 1024;

/** JPEG quality steps tried at the primary box, then at the fallback box. */
const PRIMARY_QUALITIES = [0.82, 0.7, 0.6, 0.5];
const FALLBACK_QUALITIES = [0.6, 0.5, 0.4];

const OUTPUT_MIME = "image/jpeg";

/** Loads a File into a drawable bitmap, preferring createImageBitmap with an <img> fallback. */
const loadImage = async (
	file: File
): Promise<{
	width: number;
	height: number;
	draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
	close: () => void;
}> => {
	if (typeof createImageBitmap === "function") {
		const bitmap = await createImageBitmap(file);

		return {
			width: bitmap.width,
			height: bitmap.height,
			draw: (ctx: CanvasRenderingContext2D, w: number, h: number): void => ctx.drawImage(bitmap, 0, 0, w, h),
			close: (): void => bitmap.close()
		};
	}

	const url = URL.createObjectURL(file);
	try {
		const image = await new Promise<HTMLImageElement>(
			(resolve: (value: HTMLImageElement) => void, reject: (reason: Error) => void) => {
				const element = new Image();
				element.onload = (): void => resolve(element);
				element.onerror = (): void => reject(new Error("Could not read the selected image."));
				element.src = url;
			}
		);

		return {
			width: image.naturalWidth,
			height: image.naturalHeight,
			draw: (ctx: CanvasRenderingContext2D, w: number, h: number): void => ctx.drawImage(image, 0, 0, w, h),
			close: (): void => undefined
		};
	} finally {
		URL.revokeObjectURL(url);
	}
};

/** Fits (width, height) inside a square box without upscaling, preserving aspect ratio. */
const fitWithin = (width: number, height: number, box: number): { width: number; height: number } => {
	const scale = Math.min(1, box / Math.max(width, height));

	return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

const canvasToBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> =>
	new Promise((resolve: (blob: Blob | null) => void) => {
		canvas.toBlob(resolve, OUTPUT_MIME, quality);
	});

const withJpgExtension = (name: string): string => {
	const base = name.replace(/\.[^./\\]+$/, "");

	return `${base || "image"}.jpg`;
};

/**
 * Resize + compress a freshly selected image to a small JPEG (≤ ~100 KB) before upload. Preserves
 * aspect ratio and never upscales. Falls back to the smallest acceptable result if the target can't
 * be met exactly. Throws if the browser can't render the image to a canvas.
 */
export const compressImage = async (file: File): Promise<File> => {
	const source = await loadImage(file);

	try {
		let best: Blob | null = null;

		const tryBox = async (box: number, qualities: number[]): Promise<Blob | null> => {
			const { width, height } = fitWithin(source.width, source.height, box);
			const canvas = document.createElement("canvas");
			canvas.width = width;
			canvas.height = height;

			const ctx = canvas.getContext("2d");
			if (!ctx) {
				throw new Error("Canvas is not available for image compression.");
			}
			source.draw(ctx, width, height);

			for (const quality of qualities) {
				// Sequential by design: each quality step is only tried if the previous was still too large.
				// eslint-disable-next-line no-await-in-loop
				const blob = await canvasToBlob(canvas, quality);
				if (!blob) {
					continue;
				}
				// Track the smallest encoding so a best-effort result is returned even if nothing fits.
				if (!best || blob.size < best.size) {
					best = blob;
				}
				if (blob.size <= TARGET_BYTES) {
					return blob;
				}
			}

			return null;
		};

		const primary = await tryBox(MAX_DIMENSION, PRIMARY_QUALITIES);
		const fitted = primary ?? (await tryBox(FALLBACK_DIMENSION, FALLBACK_QUALITIES));
		const output = fitted ?? best;

		if (!output) {
			throw new Error("Could not compress the selected image.");
		}

		return new File([output], withJpgExtension(file.name), { type: OUTPUT_MIME });
	} finally {
		source.close();
	}
};
